import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AddPageForm,
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
} from '@components';
import { usePageTemplates } from '@hooks';
import { pageMutation, siteQuery } from '@queries';
import { ChevronRightIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { errorsFromMutate } from '@Forms/utils.js';

export const AddPage = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);
  
  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage]);
  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const { data: pageTemplates, isPending: pageTemplatesIsPending } =
    usePageTemplates(siteID, siteData?.theme_id);

  // save datamodel
  const mutation = useMutation(
    pageMutation(null, {
      // on trigger
      onMutate: (data) => {
        // Clear error message when attempting a new submission
        setErrorMessage(null);
      },
      onSuccess: (data) => {
        // invalidate list views so they are re-fetched
        queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
        if (data.type == 'template_page') {
          // navigate to the edit view
          navigate(`/dashboard/sites/${siteID}/pages/${data.id}`);
        } else {
          // navigate to the pages list
          navigate(`/dashboard/sites/${siteID}/pages`);
        }
      },
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if(!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  const isPending = siteIsPending || pageTemplatesIsPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/pages`}>Pages</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Add')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message closable={true} messageStyle="error" title={'Something Went Wrong'} isFormMessage={false} message={errorMessage}/>
              </div>
            )}
            {isPending && <Loader className="sb-loader sb-loader-large" />}
            {pageTemplates && siteData && (
              <AddPageForm
                disabled={mutation.isPending}
                mutation={mutation}
                pageTemplates={pageTemplates}
                siteData={siteData}
                onCancel={() => {
                  navigate(`/dashboard/sites/${siteID}/pages`);
                }}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
